import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Button,
  Collapse,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Alert,
  LinearProgress,
  Link,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Tune as TuneIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

import { useData } from '../context/DataContext';
import PageHeader from '../components/ui/PageHeader';
import CategoryChip from '../components/ui/CategoryChip';
import { TIME_FRAMES } from '../components/ui/FilterBar';
import useTeamOptions from '../hooks/useTeamOptions';

// Base URL for ticket deep-links. Configurable so the app is not tied to one
// Jira tenant; falls back to the ROLLER instance for existing deployments.
const JIRA_BROWSE_URL =
  process.env.REACT_APP_JIRA_BASE_URL || 'https://rollerdigital.atlassian.net';

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/**
 * Renders effort in hours. Logged time is authoritative; when absent we show
 * the estimate and mark it so the two are never confused.
 */
const EffortCell = ({ estimatedEffort, timeSpent }) => {
  if (timeSpent > 0) {
    return <span>{(timeSpent / 3600).toFixed(1)}h</span>;
  }
  if (estimatedEffort) {
    return (
      <Tooltip title="Estimated — no time logged against this ticket">
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {estimatedEffort.toFixed(1)}h
          <Box component="span" sx={{ color: 'warning.main', ml: 0.25 }}>
            *
          </Box>
        </Box>
      </Tooltip>
    );
  }
  return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;
};

const Tickets = () => {
  const {
    tickets,
    filteredTickets,
    ticketTotal,
    filterOptions,
    setFilterOptions,
    fetchTickets,
    applyLocalFilter,
    clearFilters,
    loading,
    error,
  } = useData();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const uniqueValues = useMemo(() => {
    const list = tickets || [];
    const uniq = (key) => [...new Set(list.map((t) => t[key]).filter(Boolean))].sort();
    return { assignees: uniq('assignee'), epics: uniq('parentEpic'), categories: uniq('category') };
  }, [tickets]);

  // The dataset is far too large to hold client-side, so every page change
  // asks the server for just that slice.
  useEffect(() => {
    fetchTickets({}, { page: page + 1, limit: rowsPerPage });
  }, [fetchTickets, page, rowsPerPage]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilterOptions((prev) => ({ ...prev, [name]: value }));
  };

  const teamOptions = useTeamOptions();

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    applyLocalFilter({ search: event.target.value });
  };

  const visibleRows = useMemo(
    () => filteredTickets || [],
    [filteredTickets]
  );

  const activeFilterCount = [
    filterOptions.squad,
    filterOptions.assignee,
    filterOptions.parentEpic,
    filterOptions.category,
  ].filter(Boolean).length;

  return (
    <Box>
      <PageHeader
        title="Tickets"
        subtitle={`${(ticketTotal || 0).toLocaleString()} tickets match the current filters`}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by key, summary or description"
              value={searchTerm}
              onChange={handleSearchChange}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setSearchTerm('');
                          applyLocalFilter({ search: '' });
                        }}
                        size="small"
                        aria-label="Clear search"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<TuneIcon />}
                onClick={() => setFiltersExpanded((v) => !v)}
                aria-expanded={filtersExpanded}
              >
                Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
              </Button>
              <Button variant="contained" onClick={() => fetchTickets(filterOptions)} disabled={loading}>
                Apply
              </Button>
              <Button
                variant="text"
                color="inherit"
                onClick={() => {
                  setSearchTerm('');
                  clearFilters();
                }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>

          <Collapse in={filtersExpanded}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Team"
                  name="squad"
                  value={filterOptions.squad || ''}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All teams</MenuItem>
                  {teamOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Assignee"
                  name="assignee"
                  value={filterOptions.assignee || ''}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All assignees</MenuItem>
                  {uniqueValues.assignees.map((a) => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Parent epic"
                  name="parentEpic"
                  value={filterOptions.parentEpic || ''}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All epics</MenuItem>
                  {uniqueValues.epics.map((e) => (
                    <MenuItem key={e} value={e}>
                      {e}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Category"
                  name="category"
                  value={filterOptions.category || ''}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All categories</MenuItem>
                  {uniqueValues.categories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Time frame"
                  name="timeFrame"
                  value={filterOptions.timeFrame || ''}
                  onChange={handleFilterChange}
                >
                  {TIME_FRAMES.filter((t) => t.value !== 'custom').map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
                <TableCell sx={{ minWidth: 96 }}>Key</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Summary</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Status</TableCell>
                <TableCell sx={{ minWidth: 130 }}>Assignee</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Epic</TableCell>
                <TableCell sx={{ minWidth: 96 }}>Created</TableCell>
                <TableCell sx={{ minWidth: 96 }}>Resolved</TableCell>
                <TableCell align="right" sx={{ minWidth: 88 }}>
                  Effort
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length ? (
                visibleRows.map((ticket) => (
                  <TableRow key={ticket.key} hover>
                    <TableCell>
                      <CategoryChip category={ticket.category} />
                    </TableCell>
                    <TableCell>
                      <Link
                        href={`${JIRA_BROWSE_URL}/browse/${ticket.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                          color: 'primary.light',
                          '& svg': { fontSize: 13, opacity: 0, transition: 'opacity 120ms' },
                          '&:hover svg': { opacity: 1 },
                        }}
                      >
                        {ticket.key}
                        <OpenInNewIcon />
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 320,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={ticket.summary}
                    >
                      {ticket.summary}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {ticket.status}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>
                      {ticket.assignee || <Box component="span" sx={{ color: 'text.disabled' }}>Unassigned</Box>}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>{ticket.parentEpic || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                      {formatDate(ticket.created)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                      {formatDate(ticket.resolved)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      <EffortCell estimatedEffort={ticket.estimatedEffort} timeSpent={ticket.timeSpent} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      {(tickets || []).length === 0
                        ? 'No tickets loaded yet — import a Jira export or run a sync from Admin.'
                        : 'No tickets match the current filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 250]}
          component="div"
          count={ticketTotal || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        <Box component="span" sx={{ color: 'warning.main' }}>
          *
        </Box>{' '}
        Estimated effort, calculated during import or sync when no time was logged.
      </Typography>
    </Box>
  );
};

export default Tickets;
